import React, { useMemo, useState } from 'react';
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline';
import { generateTimeOptions, formatTimeAMPM, formatDuration } from '../../utils/formatters';
import { usePolicy } from '../../contexts/PolicyContext';

/**
 * Shared "reserve this room for myself" fields, used by both the create and
 * edit availability modals.
 *
 * The booking is always an explicit window inside the availability range —
 * never the whole range by default. Group size and client notification are
 * tucked behind "More options" so the common case is two selects and a
 * summary line.
 */

export const MAX_SELF_BOOKING_MINUTES = 480;
export const DEFAULT_SELF_BOOKING_MINUTES = 60;

export const toMinutes = (hhmm: string): number => {
  const [h, m] = hhmm.split(':').map(Number);
  return h * 60 + m;
};
export const toHHMM = (mins: number): string =>
  `${String(Math.floor(mins / 60)).padStart(2, '0')}:${String(mins % 60).padStart(2, '0')}`;

export interface SelfBookingValues {
  start: string;          // HH:MM, inside [windowStart, windowEnd)
  duration: number;       // minutes, 15-min steps
  groupSize: number;      // people using the room, including the clinician
  notifyClient: boolean;
  clientName: string;
  clientEmail: string;
}

export const defaultSelfBookingValues = (windowStart: string, windowEnd: string): SelfBookingValues => {
  const len = Math.max(15, toMinutes(windowEnd) - toMinutes(windowStart));
  return {
    start: windowStart,
    duration: Math.min(DEFAULT_SELF_BOOKING_MINUTES, len),
    groupSize: 1,
    notifyClient: false,
    clientName: '',
    clientEmail: '',
  };
};

/** Clamp a value set to a (possibly new) window. */
export const clampSelfBooking = (v: SelfBookingValues, windowStart: string, windowEnd: string): SelfBookingValues => {
  const ws = toMinutes(windowStart);
  const we = toMinutes(windowEnd);
  const start = Math.min(Math.max(toMinutes(v.start), ws), Math.max(ws, we - 15));
  const maxDuration = Math.min(MAX_SELF_BOOKING_MINUTES, Math.max(15, we - start));
  return { ...v, start: toHHMM(start), duration: Math.min(v.duration, maxDuration) };
};

export const selfBookingEnd = (v: SelfBookingValues): string => toHHMM(toMinutes(v.start) + v.duration);

export const validateSelfBooking = (v: SelfBookingValues): string | null => {
  if (v.notifyClient && !/^\S+@\S+\.\S+$/.test(v.clientEmail.trim())) {
    return "Enter your client's email address";
  }
  return null;
};

interface SelfBookingFieldsProps {
  windowStart: string;   // HH:MM — start of the availability this booking sits in
  windowEnd: string;     // HH:MM
  value: SelfBookingValues;
  onChange: (next: SelfBookingValues) => void;
  disabled?: boolean;
  clientEmailError?: string | null;
}

const selectClass =
  'w-full px-3 py-2 border border-clinic-primary-light rounded-lg bg-white text-clinic-dark ' +
  'focus:outline-none focus:ring-2 focus:ring-clinic-primary/30 focus:border-clinic-primary ' +
  'disabled:bg-clinic-surface-sunken disabled:text-clinic-muted';

const inputClass =
  'w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-clinic-primary/30 focus:border-clinic-primary';

const SelfBookingFields: React.FC<SelfBookingFieldsProps> = ({
  windowStart, windowEnd, value, onChange, disabled = false, clientEmailError,
}) => {
  const [showMore, setShowMore] = useState(value.groupSize > 1 || value.notifyClient);

  const windowEndMins = toMinutes(windowEnd);
  const windowLength = Math.max(0, windowEndMins - toMinutes(windowStart));

  // Every 15 min inside the window that leaves room for at least one slot.
  const startOptions = useMemo(
    () => generateTimeOptions(windowStart, windowEnd).filter(o => toMinutes(o.value) + 15 <= windowEndMins),
    [windowStart, windowEnd, windowEndMins]
  );
  const maxDuration = Math.min(MAX_SELF_BOOKING_MINUTES, Math.max(15, windowEndMins - toMinutes(value.start)));
  const durationOptions = useMemo(
    () => Array.from({ length: Math.floor(maxDuration / 15) }, (_, i) => (i + 1) * 15),
    [maxDuration]
  );

  const set = (patch: Partial<SelfBookingValues>) =>
    onChange(clampSelfBooking({ ...value, ...patch }, windowStart, windowEnd));

  const end = selfBookingEnd(value);
  const coversWholeWindow = windowLength > 0 && value.duration >= windowLength;

  return (
    <div className="space-y-4" data-guide="selfbook-window">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label htmlFor="self-book-start" className="block text-sm font-medium text-clinic-dark mb-1">
            Booking starts
          </label>
          <select
            id="self-book-start"
            value={value.start}
            onChange={(e) => set({ start: e.target.value })}
            className={selectClass}
            disabled={disabled}
          >
            {startOptions.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor="self-book-duration" className="block text-sm font-medium text-clinic-dark mb-1">
            Duration
          </label>
          <select
            id="self-book-duration"
            value={value.duration}
            onChange={(e) => set({ duration: parseInt(e.target.value, 10) })}
            className={selectClass}
            disabled={disabled}
          >
            {durationOptions.map(m => (
              <option key={m} value={m}>{formatDuration(m)}</option>
            ))}
          </select>
        </div>
      </div>

      <p className="text-sm text-clinic-dark" data-testid="self-book-summary">
        Booking <strong>{formatTimeAMPM(value.start)} – {formatTimeAMPM(end)}</strong> ({formatDuration(value.duration)}).
        {!coversWholeWindow && (
          <span className="text-clinic-muted"> The rest of {formatTimeAMPM(windowStart)} – {formatTimeAMPM(windowEnd)} stays open for clients.</span>
        )}
      </p>

      {coversWholeWindow && windowLength > DEFAULT_SELF_BOOKING_MINUTES && (
        <p className="text-sm text-clinic-gold">
          <strong>Heads up:</strong> this reserves your entire {formatDuration(windowLength)} block and you'll be
          billed for all of it. Shorten the duration if you only need part of it.
        </p>
      )}

      {/* More options: group size + notify a client */}
      <div>
        <button
          type="button"
          onClick={() => setShowMore(s => !s)}
          className="inline-flex items-center gap-1 text-sm text-clinic-primary hover:text-clinic-primary-dark"
          aria-expanded={showMore}
          disabled={disabled}
        >
          {showMore ? <ChevronUpIcon className="h-4 w-4" /> : <ChevronDownIcon className="h-4 w-4" />}
          More options
          {!showMore && (value.groupSize > 1 || value.notifyClient) && (
            <span className="text-clinic-muted">
              {' '}· {value.groupSize > 1 ? `${value.groupSize} people` : ''}
              {value.groupSize > 1 && value.notifyClient ? ', ' : ''}
              {value.notifyClient ? 'client notified' : ''}
            </span>
          )}
        </button>

        {showMore && (
          <div className="mt-3 space-y-3 pl-1">
            <div className="flex items-center justify-between gap-3">
              <label htmlFor="self-book-group" className="text-sm text-clinic-dark">
                People using the room
                <span className="block text-xs text-clinic-muted">Including you. We'll assign a room that fits.</span>
              </label>
              <input
                id="self-book-group"
                aria-label="People using the room"
                type="number"
                min={1}
                max={50}
                value={value.groupSize}
                onChange={(e) => set({ groupSize: Math.max(1, parseInt(e.target.value) || 1) })}
                className="w-16 px-2 py-1.5 text-center border border-clinic-primary-light rounded-lg focus:outline-none focus:ring-2 focus:ring-clinic-primary/30 focus:border-clinic-primary"
                disabled={disabled}
              />
            </div>

            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={value.notifyClient}
                onChange={(e) => set({ notifyClient: e.target.checked })}
                disabled={disabled}
                className="mt-0.5 h-4 w-4 text-clinic-primary rounded border-clinic-primary-light focus:ring-clinic-primary/30"
              />
              <span className="text-sm text-clinic-dark">
                Notify a client
                <span className="block text-xs text-clinic-muted">They get the confirmation and reminders by email.</span>
              </span>
            </label>

            {value.notifyClient && (
              <div className="space-y-2">
                <input
                  type="text"
                  value={value.clientName}
                  onChange={(e) => set({ clientName: e.target.value })}
                  maxLength={200}
                  placeholder="Client name (optional)"
                  aria-label="Client name (optional)"
                  className={`${inputClass} border-clinic-primary-light`}
                  disabled={disabled}
                />
                <input
                  type="email"
                  value={value.clientEmail}
                  onChange={(e) => set({ clientEmail: e.target.value })}
                  maxLength={254}
                  placeholder="Client email"
                  aria-label="Client email"
                  className={`${inputClass} ${clientEmailError ? 'border-clinic-accent' : 'border-clinic-primary-light'}`}
                  disabled={disabled}
                />
                {clientEmailError && <p className="text-sm text-clinic-accent">{clientEmailError}</p>}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * The one-line commitment note under the self-booking fields. Reads the live
 * cancellation policy so the wording always matches what the server enforces:
 * while self-cancel is enabled, a self-booking can be cancelled from My
 * Bookings, free outside the window and billed inside it. If the switch is
 * ever off again, fall back to the old "can't be cancelled" wording.
 */
export const SelfBookingPolicyNote: React.FC<{ durationMinutes?: number }> = ({ durationMinutes }) => {
  const { windowHours, graceMinutes, selfCancelEnabled } = usePolicy();
  const billed = durationMinutes ? `billed for ${formatDuration(durationMinutes)}` : 'billed for it';
  if (!selfCancelEnabled) {
    return (
      <p className="text-xs text-clinic-gold" data-testid="self-book-policy-note">
        Once confirmed you're committed to this time and {billed}. Self-bookings can't be cancelled.
      </p>
    );
  }
  return (
    <p className="text-xs text-clinic-gold" data-testid="self-book-policy-note">
      Once confirmed you're {billed}. You can cancel from My Bookings: more than {windowHours} hours
      before the start the room is released at no charge; inside {windowHours} hours the room stays
      reserved for you and is still billed.
      {graceMinutes > 0 && <> Booked by mistake? Cancel within {graceMinutes} minutes and it's free.</>}
    </p>
  );
};

export default SelfBookingFields;
